import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Download, Filter, Search, Tag, ChevronDown, CalendarRange, Eye, Edit, Trash2 } from 'lucide-react';
import { getExpenses, deleteExpense, getExpenseCategories } from '../../../../services/finance_services/expenseService';
import { exportExpensesListExcel } from '../../../../services/export_services/expenseExportService';
import useApi from '../../../../hooks/useApi';
import Message from '../../../../components/Message';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { formatDate } from '../../../../utils/date';
import ReportTable from '../../../../components/ReportTable';
import Pagination from '../../../../components/Pagination';

const ExpenseReportPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Separate temporary filters (what user is typing) from applied filters
  const [tempFilters, setTempFilters] = useState({
    category: '',
    dateRange: '',
    startDate: '',
    endDate: '',
  });
  
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    category: '',
    dateRange: '',
    startDate: '',
    endDate: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Use useApi hooks for different operations
  const {
    data: expensesData,
    loading,
    error,
    callApi: fetchExpenses,
    setError
  } = useApi(getExpenses, {showSuccessToast: false});

  const {
    data: categories,
    loading: loadingCategories,
    callApi: fetchCategories
  } = useApi(getExpenseCategories, {showSuccessToast: false});

  const {
    loading: deleteLoading,
    error: deleteError,
    callApi: removeExpense,
    setError: setDeleteError
  } = useApi(deleteExpense, {showSuccessToast: true, msg: 'Expense category deleted'});

  // Extract expenses from the response data
  const expenses = expensesData?.results || [];

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch expenses whenever pagination or applied filters change
  useEffect(() => {
    const loadExpenses = async () => {
      const filters = {
        search: appliedFilters.search,
        category: appliedFilters.category,
        date_range: appliedFilters.dateRange,
        start_date: appliedFilters.startDate,
        end_date: appliedFilters.endDate,
      };
      
      const response = await fetchExpenses(pagination.page, pagination.page_size, filters);
      
      if (response) {
        setPagination(prev => ({
          ...prev,
          total_pages: response.total_pages || 0,
          count: response.count || 0
        }));
      }
    };

    loadExpenses();
  }, [pagination.page, pagination.page_size, appliedFilters, fetchExpenses]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleDeleteExpense = (expense) => {
    setExpenseToDelete(expense);
    setIsDeleteModalOpen(true);
    setDeleteError(null);
  };

  const confirmDeleteExpense = async () => {
    setIsDeleteModalOpen(false);
    try {
      await removeExpense(expenseToDelete.id);
      // Refetch the current page directly
      const filters = {
        search: appliedFilters.search,
        category: appliedFilters.category,
        date_range: appliedFilters.dateRange,
        start_date: appliedFilters.startDate,
        end_date: appliedFilters.endDate,
      };
      const response = await fetchExpenses(pagination.page, pagination.page_size, filters);
      if (response) {
        setPagination(prev => ({
          ...prev,
          total_pages: response.total_pages || 0,
          count: response.count || 0
        }));
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete expense');
    } finally {
      setExpenseToDelete(null);
    }
  };

  const cancelDeleteExpense = () => {
    setIsDeleteModalOpen(false);
    setExpenseToDelete(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    // Apply filters and reset to page 1
    setAppliedFilters({
      search: searchTerm,
      category: tempFilters.category,
      dateRange: tempFilters.dateRange,
      startDate: tempFilters.startDate,
      endDate: tempFilters.endDate,
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilters = () => {
    setTempFilters({
      category: '',
      dateRange: '',
      startDate: '',
      endDate: '',
    });
    setSearchTerm('');
    setAppliedFilters({
      search: '',
      category: '',
      dateRange: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExportExpenses = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const filters = {
        start_date: appliedFilters.startDate,
        end_date: appliedFilters.endDate,
      };
      
      const result = await exportExpensesListExcel(filters);
      if (result.success) {
        // Success message is handled by the download
      } else {
        setExportError(result.error || 'Failed to export expenses to Excel.');
      }
    } catch (err) {
      setExportError(err.message || 'Failed to export expenses to Excel.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { label: 'Title', key: 'title' },
    { label: 'Category', key: 'category_name' },
    { label: 'Amount', key: 'amount' },
    { label: 'Date', key: 'date_of_expense', format: formatDate },
    { label: 'Vendor', key: 'vendor' },
    { label: 'Payment Method', key: 'payment_method' },
    {
      key: 'actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            className="text-primary-600 hover:bg-primary-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/expense-reports/${row.id}`)}
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            className="text-tertiary-600 hover:bg-tertiary-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/expense-reports/${row.id}/edit`)}
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            className="text-red-600 hover:bg-red-100 p-1 rounded"
            onClick={() => handleDeleteExpense(row)}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Reports</h1>
          <p className="text-gray-600">Manage and track your expenses</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <Link 
            to="/dashboard/expense-categories" 
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Tag className="h-4 w-4 mr-2" />
            Categories
          </Link>
          <button 
            onClick={() => navigate('/dashboard/expense-reports/new')} 
            className="inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search expenses..." 
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  applyFilters();
                }
              }}
            />
          </div>
          
          <div className="flex gap-2">
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2 text-gray-500" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 ml-2 text-gray-500 transition-transform ${showFilters ? 'transform rotate-180' : ''}`} />
            </button>
            
            <button 
              onClick={resetFilters} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            
            <button 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center text-gray-700 hover:bg-gray-50"
              onClick={handleExportExpenses}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4 mr-2 text-gray-500" />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
        </div>
        
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Category filter */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  id="category"
                  name="category"
                  className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={tempFilters.category}
                  onChange={handleFilterChange}
                  disabled={loadingCategories}
                >
                  <option value="">All Categories</option>
                  {(categories || []).map(category => (
                    <option key={category.id} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Date Range filter */}
              <div>
                <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 mb-1">Expense date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarRange className="h-4 w-4 text-gray-400" />
                  </div>
                  <select
                    id="dateRange"
                    name="dateRange"
                    className="w-full pl-10 border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={tempFilters.dateRange}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Custom date range inputs */}
            {tempFilters.dateRange === 'custom' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={tempFilters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={tempFilters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
            )}
            
            {/* Apply Filters Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <Message type="error" message={error || deleteError} />
      <Message type="error" message={exportError} />
      
      <ReportTable 
        title="Expense Reports"
        data={expenses}
        columns={columns}
        loading={loading}
        emptyMessage="No expenses found. Try adjusting your search or filter criteria."
        onView={(expense) => navigate(`/dashboard/expense-reports/${expense.id}`)}
        onAdd={() => navigate('/dashboard/expense-reports/new')}
        onEdit={(expense) => navigate(`/dashboard/expense-reports/${expense.id}/edit`)}
        onDelete={handleDeleteExpense}
        setError={setError}
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Expenses"
      />
      
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeleteExpense}
        onConfirm={confirmDeleteExpense}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense? This action cannot be undone.`}
      />
    </div>
  );
};

export default ExpenseReportPage;






